<template>
  <div class="col-12">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3"  style="font-size: 0.9rem; font-size: 1rem">
            {{ t('students.details_title') }}
          </h6>
        </div>
      </div>

      <!-- Loading state -->
      <LoadingSpinner v-if="isLoading" :message="t('students.loading_student')" />

      <!-- Content when loaded -->
      <div v-else class="card-body px-0 pb-2 pe-3">
        <div class="p-4">
          <div v-if="student.id" class="row g-3">
            <!-- Student Image -->
            <div class="col-12 text-center mb-4">
              <img 
                :src="studentImageUrl"
                :alt="t('students.image_alt')"
                class="student-avatar"
                @error="handleImageError"
              />
            </div>
  
            <!-- Name Fields -->
            <div class="col-md-4">
              <label class="bmd-label-float">{{ t('students.first_name') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.first_name }}
              </div>
            </div>
            
            <div class="col-md-4">
              <label class="bmd-label-float">{{ t('students.father_name') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.father_name }}
              </div>
            </div>
            
            <div class="col-md-4">
              <label class="bmd-label-float">{{ t('students.last_name') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.last_name }}
              </div>
            </div>
  
            <!-- Personal Information -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.mother_name') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.mother_name }}
              </div>
            </div>
            
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.birthdate') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ formatDate(student.birthdate) }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.gender') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.gender === 'M' ? t('students.male') : t('students.female') }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.nationality') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.nationality?.name || t('students.not_specified') }}
              </div>
            </div>

            <!-- National ID or Passport -->
            <div class="col-md-6" v-if="isLibyan">
              <label class="bmd-label-float">{{ t('students.national_num') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.national_num || t('students.not_specified') }}
              </div>
            </div>

            <div class="col-md-6" v-else>
              <label class="bmd-label-float">{{ t('students.passport_num') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.passport_num || t('students.not_specified') }}
              </div>
            </div>

             <!-- Current Academic Year -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.current_academic_year') }}</label>
              <div class="form-control-plaintext borderless-input">
                <span v-if="student.current_academic_year">
                  {{ student.current_academic_year.name }} ({{ student.current_academic_year.year }})
                  <span v-if="student.current_academic_year.is_active" class="badge badge-sm bg-success ms-2">
                    {{ t('students.active') }}
                  </span>
                </span>
                <span v-else class="text-muted">{{ t('students.not_specified') }}</span>
              </div>
            </div>
            
            <!-- Class and Group Information -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.class') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.current_assignment?.class?.name || t('students.not_specified') }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.group') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.current_assignment?.group?.name || t('students.not_specified') }}
              </div>
            </div>

            <!-- Contact Information -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.email') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.email || t('students.not_specified') }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.phone') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.phone || t('students.not_specified') }}
              </div>
            </div>

            <!-- Address -->
            <div class="col-12">
              <label class="bmd-label-float">{{ t('students.address') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.address || t('students.not_specified') }}
              </div>
            </div>

            <!-- Chronic Diseases -->
            <div class="col-12" v-if="student.chronic_disease">
              <label class="bmd-label-float">{{ t('students.chronic_disease') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.chronic_disease }}
              </div>
            </div>

            <!-- dietary_restrictions -->
            <div class="col-12" v-if="student.dietary_restrictions">
              <label class="bmd-label-float">{{ t('students.dietary_restrictions') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ student.dietary_restrictions }}
              </div>
            </div>
             
            <!-- Registration Date -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('students.register_date') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ formatDate(student.register_date) }}
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="col-12 mt-4">
              <div class="d-flex gap-3">
                <router-link
                  :to="`/students/${student.id}/edit`"
                  class="btn btn-primary btn-sm flex-grow-1"
                  v-can="'students.update'"
                >
                  <i class="fas fa-edit ms-2"></i>
                  {{ t('students.edit_button') }}
                </router-link>
                <button 
                  @click="router.back()" 
                  class="btn btn-secondary btn-sm flex-grow-1"
                >
                  <i class="fas fa-arrow-right ms-2"></i>
                  {{ t('students.back') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Error state when student not found -->
          <div v-else class="text-center py-4">
            <div class="alert alert-danger">
              {{ t('students.not_found') }}
            </div>
            <button class="btn btn-secondary" @click="router.push('/students')">
              {{ t('students.back_to_students') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import { useToast } from 'vue-toastification'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const isLoading = ref(true)

const student = ref({
  id: null,
  first_name: '',
  father_name: '',
  last_name: '',
  mother_name: '',
  birthdate: '',
  address: '',
  national_num: '',
  passport_num: '',
  image_url: '',
  chronic_disease: '',
  dietary_restrictions: '',
  email: '',
  phone: '',
  register_date: '',
  gender: '',
  nationality: null,
  current_assignment: null,
  class_groups: []
})

const isLibyan = computed(() => {
  return student.value.nationality?.code === 'LY'
})

const placeholderImage = '/images/default-avatar.png'
const studentImageUrl = computed(() => {
  if (!student.value.image_url) return placeholderImage
  return student.value.image_url.includes('http') 
    ? student.value.image_url 
    : `${import.meta.env.VITE_API_BASE_URL || ''}${student.value.image_url}`
})

const handleImageError = (e) => {
  e.target.src = placeholderImage
}

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value || 'ar', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
onMounted(async () => {
  try {
    const studentId = route.params.id
    if (!studentId) {
      toast.error(t('students.invalid_id'))
      router.push('/students')
      return
    }

    const response = await api.get(`/students/${studentId}`, {
      params: {
        include: 'nationality,currentAssignment.class,currentAssignment.group'
      }
    })
    
    student.value = response.data.data
  } catch (error) {
    console.error('Error loading student:', error)
    toast.error(t('students.load_failed_details'))
  } finally {
    isLoading.value = false
  }
})
</script>

<style scoped>
.student-avatar {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #eee;
}

.borderless-input {
  border-bottom: 1px solid #ddd;
  padding: 8px 0;
  min-height: 40px;
}
</style>